import { compileSkyAuthoredRig, sampleSkyAuthoredPose } from './115-sky-authored-pose.mjs';

const KINDS = ['fuel', 'cannon', 'shield'];
const CLIPS = [
  'Idle',
  'FuelLift',
  'CannonLift',
  'ShieldLift',
  'CraneSweep',
  'ClawClose',
  'Reset',
  'FuelRupture',
];
const axes = ['x', 'y', 'z'];
const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
const smooth = (value) => {
  const q = clamp(value, 0, 1);
  return q * q * (3 - 2 * q);
};
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
const add = (a, b, t = 1) => Object.fromEntries(axes.map((axis) => [axis, a[axis] + b[axis] * t]));
const zero = () => ({ x: 0, y: 0, z: 0 });
const identity = (position) => ({ position, quaternion: [0, 1, 0, 0], scale: [1, 1, 1] });
const masks = new WeakMap();
export const SKY_SCRAP = Object.freeze({
  approach: 4,
  survey: 7,
  tell: 1.5,
  intake: 3,
  assembly: 2.5,
  shield: 4,
  fire: 2.4,
  recovery: 7,
  overload: 10,
  supplyHp: 8,
  maxOffset: 12,
});

function sourceMask(rig) {
  if (masks.has(rig)) return masks.get(rig);
  const mask = rig.meshes.map((mesh) => {
    if (!mesh.jointNodes) return null;
    const result = [];
    for (let i = 0; i < mesh.indices.length; i += 3) {
      const owners = mesh.indices.slice(i, i + 3).map((vertex) => {
        let owner = -1;
        for (let slot = 0; slot < 4; slot++)
          if (mesh.weights[vertex * 4 + slot] > 0.000001) {
            if (Math.abs(mesh.weights[vertex * 4 + slot] - 1) > 0.000001 || owner !== -1) return -1;
            owner = mesh.jointNodes[mesh.joints[vertex * 4 + slot]];
          }
        return owner;
      });
      if (owners[0] < 0 || owners.some((owner) => owner !== owners[0])) return null;
      result.push(rig.nodes[owners[0]].name);
    }
    return result;
  });
  if (
    mask.some((row) => !row) ||
    !KINDS.every((kind) => mask.some((row) => row.includes('supply_' + kind + '_joint')))
  )
    return null;
  masks.set(rig, mask);
  return mask;
}

/** Readiness accepts the original rigid-weight salvage platform only. Every
 * gameplay supply owns real source triangles, clips and a surface socket. */
export function installSkyScrapSupplyRig(state, source) {
  const rig = compileSkyAuthoredRig(source);
  if (
    !state ||
    rig?.worldId !== 'scrap-supplies' ||
    !sourceMask(rig) ||
    !CLIPS.every((name) => rig.clips.some((clip) => clip.name === name)) ||
    ![...KINDS.map((kind) => kind + '_supply'), 'claw_contact', 'claw_control'].every((name) =>
      rig.sockets.some((socket) => socket.name === name),
    )
  )
    return false;
  state.authoredPropRigs ??= {};
  state.authoredPropRigs['scrap-supplies'] = rig;
  return true;
}
function refreshBody(prop) {
  const bounds = prop.authoredPose.bounds;
  prop.body = {
    x: (bounds.min[0] + bounds.max[0]) / 2,
    y: (bounds.min[1] + bounds.max[1]) / 2,
    z: bounds.max[2],
    radius: Math.max(...bounds.max.map((n, i) => n - bounds.min[i])) / 2,
  };
}
/** Fixed orbital salvage arena. Boss and platform retain original metre scale;
 * moving supply joints share the numeric pose used for rendering and contacts. */
export function createSkyScrapEmperor(state, boss) {
  const rig = state.authoredPropRigs?.['scrap-supplies'];
  if (boss?.worldId !== 'scrap-emperor' || !rig || !boss.authoredRig) return false;
  const prop = {
    id: 'scrap-supplies',
    kind: 'authored-prop',
    artVariant: 'scrap-supplies',
    worldId: 'scrap-supplies',
    authoredRig: rig,
    scrapSupply: true,
    hp: 24,
    maxHp: 24,
    collisionEnabled: true,
    rootTransform: identity([0, -rig.bounds.min[1], -14 + rig.bounds.min[2]]),
  };
  [prop.x, prop.y, prop.z] = prop.rootTransform.position;
  prop.authoredPose = sampleSkyAuthoredPose(rig, 'Idle', 0, prop.rootTransform);
  const rest = prop.authoredPose;
  prop.restPose = rest;
  boss.scrap = {
    phase: 'approach',
    age: 0,
    clock: 0,
    cycle: 0,
    selected: null,
    build: null,
    consumed: [],
    shots: 0,
    overrides: [],
    impacts: [],
    rootTransform: identity([
      0,
      2 - boss.authoredRig.bounds.min[1],
      -46 + boss.authoredRig.bounds.min[2],
    ]),
    prop,
    supplies: KINDS.map((kind) => ({
      kind,
      node: rig.nodes.findIndex((node) => node.name === 'supply_' + kind + '_joint'),
      hp: SKY_SCRAP.supplyHp,
      offset: zero(),
      status: 'available',
      origin: { ...rest.sockets[kind + '_supply'] },
    })),
  };
  boss.authoredProps = [prop];
  // Only original platform-owned vertices define this admission clearance;
  // the articulated crane is not a substitute deck or an invisible blocker.
  let deckTop = -Infinity;
  for (let meshIndex = 0; meshIndex < rig.meshes.length; meshIndex++) {
    const mesh = rig.meshes[meshIndex],
      vertices = rest.meshes[meshIndex].vertices;
    for (const vertex of mesh.indices) {
      const joint = mesh.jointNodes[mesh.joints[vertex * 4]];
      if (rig.nodes[joint].name === 'salvage_platform_root')
        deckTop = Math.max(deckTop, vertices[vertex * 3 + 1]);
    }
  }
  boss.scrap.entryHeight = deckTop + 4;
  if (state.startMode === 'boss') {
    state.y = boss.scrap.entryHeight;
    state.locomotion = 'fly';
  }
  refreshBody(prop);
  return true;
}
function phase(s, name, event, state) {
  s.phase = name;
  s.age = 0;
  s.shots = 0;
  const label = {
    survey: 'SALVAGE SURVEY · SHOOT SUPPLIES TO DENY OR MOVE THEM',
    tell: 'MAGNET CHARGING · THE NEAREST PILE WILL BE TAKEN',
    intake: 'SALVAGE IN MOTION · DESTROY THE SELECTED SUPPLY',
    assembly: 'REBUILDING · REACTOR EXPOSED',
    shield: 'SHIP BOW SHIELD · WAIT FOR THE BREAK',
    fire: 'ASSEMBLED CANNON · DODGE',
    recovery: 'REACTOR EXPOSED · FIRE',
    overload: 'FUEL INTAKE OVERLOAD · REACTOR EXPOSED',
  }[name];
  if (label) event(state, 'boss-phase', label);
}
function choose(s, boss) {
  const intake = boss.authoredPose.sockets.scrap_crane;
  return (
    s.supplies
      .filter((pile) => pile.status === 'available')
      .sort(
        (a, b) =>
          distance(s.prop.authoredPose.sockets[a.kind + '_supply'], intake) -
            distance(s.prop.authoredPose.sockets[b.kind + '_supply'], intake) ||
          KINDS.indexOf(a.kind) - KINDS.indexOf(b.kind),
      )[0] ?? null
  );
}
function beat(s) {
  const map = {
    approach: ['Idle', 0, false],
    survey: ['Idle', s.age % 4, false],
    tell: ['MagnetTell', s.age, false],
    intake: ['SalvageOrbit', Math.min(4, (s.age * 4) / SKY_SCRAP.intake), true],
    assembly: [s.build === 'cannon' ? 'AssembleCannon' : 'ShieldBuild', s.age, true],
    shield: ['ShieldBuild', 2, false],
    fire: ['Fire', s.age % 0.8, false],
    recovery: ['ShieldBreak', Math.min(2, s.age), true],
    overload: ['CoreExpose', Math.min(1.5, s.age), true],
  };
  const [clip, seconds, exposed] = map[s.phase];
  return { clip, seconds, exposed, target: 'reactor_core' };
}
/** Advance active-time tells and finite reconstruction opportunities. No clock
 * transition deals damage, consumes an uncontacted pile or awards completion. */
export function stepSkyScrapEmperor(state, dt, { event }) {
  const boss = state.boss,
    s = boss.scrap;
  if (!(dt > 0) || !Number.isFinite(dt)) return beat(s);
  s.age += dt;
  s.clock += dt;
  if (s.phase === 'approach' && s.age >= SKY_SCRAP.approach) phase(s, 'survey', event, state);
  else if (s.phase === 'survey' && s.age >= SKY_SCRAP.survey) phase(s, 'tell', event, state);
  else if (s.phase === 'tell' && s.age >= SKY_SCRAP.tell) {
    const selected = choose(s, boss);
    s.selected = selected?.kind ?? null;
    s.build = s.selected;
    if (selected) {
      s.intakeStart = { ...s.prop.authoredPose.sockets[selected.kind + '_supply'] };
      s.intakeTarget = { ...boss.authoredPose.sockets.scrap_crane };
      s.intakeOffset = { ...selected.offset };
      phase(s, 'intake', event, state);
    } else phase(s, 'recovery', event, state);
  } else if (s.phase === 'assembly' && s.age >= SKY_SCRAP.assembly)
    phase(s, s.build === 'shield' ? 'shield' : 'fire', event, state);
  else if (
    (s.phase === 'shield' && s.age >= SKY_SCRAP.shield) ||
    (s.phase === 'fire' && s.age >= SKY_SCRAP.fire)
  )
    phase(s, 'recovery', event, state);
  else if (
    (s.phase === 'recovery' && s.age >= SKY_SCRAP.recovery) ||
    (s.phase === 'overload' && s.age >= SKY_SCRAP.overload)
  ) {
    s.cycle++;
    s.selected = null;
    s.build = null;
    phase(s, 'survey', event, state);
  }
  return beat(s);
}
function localDelta(matrix, delta) {
  const [x, y, z] = [delta.x, delta.y, delta.z],
    a = matrix[0],
    b = matrix[4],
    c = matrix[8],
    d = matrix[1],
    e = matrix[5],
    f = matrix[9],
    g = matrix[2],
    h = matrix[6],
    i = matrix[10],
    det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  return [
    ((e * i - f * h) * x + (c * h - b * i) * y + (b * f - c * e) * z) / det,
    ((f * g - d * i) * x + (a * i - c * g) * y + (c * d - a * f) * z) / det,
    ((d * h - e * g) * x + (b * g - a * h) * y + (a * e - b * d) * z) / det,
  ];
}
function poseSupplies(s) {
  const prop = s.prop,
    rig = prop.authoredRig;
  const key = s.supplies
    .map((pile) => axes.map((axis) => pile.offset[axis]).join(',') + pile.status)
    .join('|');
  if (prop.poseKey === key) return;
  prop.poseKey = key;
  const base = prop.restPose;
  const overrides = s.supplies.map((pile) => {
    const node = base.nodes[pile.node],
      parent = rig.nodes[pile.node].parent,
      delta = localDelta(base.matrices[parent], pile.offset);
    return {
      node: pile.node,
      position: node.position.map((v, i) => v + delta[i]),
      quaternion: node.quaternion,
      scale: node.scale,
    };
  });
  prop.authoredPose = sampleSkyAuthoredPose(rig, 'Idle', 0, prop.rootTransform, { overrides });
  prop.weakpoints = s.supplies
    .filter((pile) => pile.status === 'available')
    .map((pile) => ({
      ...prop.authoredPose.sockets[pile.kind + '_supply'],
      id: pile.kind,
      label: pile.kind.toUpperCase() + ' SUPPLY',
      radius: 1.2,
      criticalRadius: 0.7,
      exposed: true,
    }));
  prop.weakpoint = prop.weakpoints[0] ?? null;
  refreshBody(prop);
}
/** Commit only source-surface intake contact, then project original supply and
 * cannon articulation. Fixed arena movement never depends on scenery travel. */
export function finishSkyScrapEmperor(state, dt, { event, fireAimed }) {
  const boss = state.boss,
    s = boss.scrap;
  if (!(dt > 0) || !Number.isFinite(dt) || boss.defeated) return;
  if (s.phase === 'intake') {
    const pile = s.supplies.find((value) => value.kind === s.selected);
    if (pile?.status !== 'available') phase(s, 'recovery', event, state);
    else {
      s.intakeTarget = { ...boss.authoredPose.sockets.scrap_crane };
      const delta = Object.fromEntries(
        axes.map((axis) => [axis, s.intakeTarget[axis] - s.intakeStart[axis]]),
      );
      pile.offset = add(s.intakeOffset, delta, smooth(s.age / SKY_SCRAP.intake));
    }
  }
  poseSupplies(s);
  if (s.phase === 'intake') {
    const pile = s.supplies.find((value) => value.kind === s.selected),
      surface = s.prop.authoredPose.sockets[pile.kind + '_supply'];
    if (distance(surface, s.intakeTarget) < 0.0001) {
      pile.status = 'consumed';
      s.consumed.push(pile.kind);
      event(state, 'scrap-intake', pile.kind.toUpperCase() + ' SUPPLY REACHED THE MAGNET');
      phase(s, pile.kind === 'fuel' ? 'overload' : 'assembly', event, state);
    }
  }
  if (s.phase === 'fire') {
    const due = Math.min(3, Math.floor(s.age / 0.8) + 1);
    while (s.shots < due) {
      fireAimed(state, boss.authoredPose.sockets.cannon_muzzle, 'paired', boss);
      s.shots++;
    }
  }
  boss.controlHint = beat(s).exposed
    ? 'RECONSTRUCTION · AIM AT THE GLOWING REACTOR'
    : `${s.supplies.filter((pile) => pile.status === 'available').length}/3 SUPPLIES LEFT · SHOOT BLUE ARMOR / CANNONS TO DENY · FEED RED FUEL`;
  boss.authoredFlightCeiling = Math.max(
    boss.authoredFlightCeiling ?? 9,
    boss.authoredPose.sockets.reactor_core.y + 5,
  );
}
/** Classify the exact nearest source triangle. Frame hits cannot damage supply
 * health, autonomous attacks cannot choose a build, and only reconstruction
 * permits actual reactor damage. Every finite pile can be consumed at most once. */
export function resolveSkyScrapContact(state, target, shot, contact, effect, { event }) {
  const boss = state.boss,
    s = boss?.scrap;
  if (!s || (target !== boss && target !== s.prop)) return null;
  if (effect.autonomous || boss.defeated || s.phase === 'approach')
    return { handled: true, damage: 0, quality: 'armor' };
  if (target === boss) {
    const core = boss.authoredPose.sockets.reactor_core;
    if (!beat(s).exposed || distance(core, contact) > 2)
      return { handled: true, damage: 0, quality: 'armor' };
    const damage = Math.min(boss.hp, Math.max(0, effect.damage));
    boss.hp -= damage;
    return { handled: true, damage, quality: 'critical' };
  }
  const role = sourceMask(target.authoredRig)?.[contact.meshIndex]?.[contact.triangleIndex];
  const pile = s.supplies.find((value) => role === 'supply_' + value.kind + '_joint');
  if (!pile || pile.status !== 'available') return { handled: true, damage: 0, quality: 'armor' };
  const damage = Math.min(pile.hp, Math.max(0, effect.damage));
  pile.hp -= damage;
  if (s.phase !== 'intake' || s.selected !== pile.kind) {
    const length = Math.hypot(shot.vx ?? 0, shot.vy ?? 0, shot.vz ?? 0);
    if (length > 0)
      axes.forEach((axis) => {
        // The original deck supports each pile; recoil can lift it but cannot
        // push a live target down through its platform and make it unshootable.
        pile.offset[axis] = clamp(
          pile.offset[axis] + ((shot['v' + axis] ?? 0) / length) * 1.2,
          axis === 'y' ? 0 : -SKY_SCRAP.maxOffset,
          SKY_SCRAP.maxOffset,
        );
      });
  }
  if (pile.hp <= 0) {
    pile.status = 'destroyed';
    event(state, 'scrap-denied', pile.kind.toUpperCase() + ' SUPPLY DESTROYED · BUILD DENIED');
  }
  return { handled: true, damage: 0, quality: damage > 0 ? 'body' : 'armor' };
}
